import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { MapPin, ArrowRight } from "lucide-react";
import { SITE, MENU } from "../data/menu";
import { useSettings } from "../site-settings";
import Order from "./Order";
import { BlossomBranch, Lantern, PetalField, Flourish, Blossom } from "../components/decor";
import { MaskedLine, Reveal } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { DishCard } from "../components/DishCard";

const CHAPTERS = [
  {
    no: "01",
    title: "A Family Table",
    text: "Bloomora began as a simple wish — a place in Dumka where families could linger over dinner the way they do at home. Two floors of blush walls, blossoms overhead, and tables made for long conversations.",
    img: "/images/interior-00.jpg",
    alt: "Blush corridor with gold-lit mirrors and the floral ceiling grid",
  },
  {
    no: "02",
    title: "Purely Vegetarian",
    text: "Every dish that leaves our kitchen is pure vegetarian — paneer simmered in silky gravies, tandoor breads pulled hot from the coals, and vegetables treated with the respect they deserve.",
    img: "/images/interior-01.jpg",
    alt: "Pink dining hall with custom lighting arches",
  },
  {
    no: "03",
    title: "Twelve Cuisines, One Kitchen",
    text: "From smoky Hakka noodles to dum-sealed biryani, from crisp dosas to cloud-soft bao — our kitchen travels across India and beyond, without ever leaving Tin Bazar Road.",
    img: "/images/interior-02.jpg",
    alt: "Aesthetic seating beneath hanging blossoms",
  },
  {
    no: "04",
    title: "Dumka's Sweet Spot",
    text: "Near Dharmsthan Mandir, under a pink neon bloom, we end every meal the way it should end — gulab jamun still warm, kulfi over rose falooda, and a promise to see you again soon.",
    img: "/images/gallery-10.jpg",
    alt: "Neon Bloomora sign under cascading blossoms",
  },
];

const Hero = () => {
  const ref = useRef(null);
  const { menuVisible } = useSettings();
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const branchY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 120]);
  const lanternY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -60]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 80]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-[92vh] items-center justify-center overflow-hidden bg-gradient-to-b from-[#F8E8E8] via-[#F6DFDF] to-[#F3D3D3] px-5"
    >
      <motion.div style={{ y: branchY }} className="pointer-events-none absolute -left-16 bottom-0 z-0 md:-left-6">
        <BlossomBranch className="h-[68vh] w-auto opacity-40 md:h-[85vh] md:opacity-90" />
      </motion.div>

      <motion.div style={{ y: lanternY }} className="pointer-events-none absolute right-4 top-0 z-0 flex gap-6 md:right-16 md:gap-10">
        <Lantern className="h-28 w-14 md:h-40 md:w-20" delay={0} />
        <Lantern className="mt-10 h-24 w-12 md:mt-16 md:h-32 md:w-16" delay={1.2} />
        <Lantern className="mt-4 hidden h-28 w-14 sm:block md:h-36 md:w-[4.5rem]" delay={2.1} />
      </motion.div>

      <PetalField count={14} />

      <motion.div style={{ y: textY }} className="relative z-10 max-w-3xl py-24 text-center">
        <MaskedLine delay={0.15}>
          <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-[#C9A227]/50 px-5 py-1.5 text-xs font-medium uppercase tracking-[0.25em] text-[#9B2C2C]">
            <Blossom className="h-3.5 w-3.5 text-[#E8B4B8]" /> Dumka • Jharkhand
          </span>
        </MaskedLine>
        <h1 data-testid="hero-title" className="font-serif text-6xl font-black leading-none tracking-tight text-[#9B2C2C] sm:text-7xl lg:text-8xl">
          <MaskedLine delay={0.3}>BLOOMORA</MaskedLine>
        </h1>
        <p data-testid="hero-hindi" className="font-hindi mt-3 text-2xl text-[#B86B77] md:text-3xl">
          <MaskedLine delay={0.5}>ब्लूमोरा</MaskedLine>
        </p>
        <div className="mt-6">
          <MaskedLine delay={0.65}>
            <p data-testid="hero-tagline" className="font-serif text-lg italic text-[#2C2C2C] md:text-xl">
              The Sweet Spot &amp; Savouries — Family Restaurant
            </p>
          </MaskedLine>
        </div>

        <motion.div
          className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row"
          initial={reduce ? false : { opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <Link
            to="/order"
            data-testid="hero-order-online-button"
            className="group relative rounded-full bg-[#9B2C2C] px-9 py-3.5 font-medium text-white shadow-[0_14px_36px_rgba(155,44,44,0.35)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#7A1D28]"
          >
            Order Food Online
            <span className="absolute bottom-2 left-1/2 h-[2px] w-0 -translate-x-1/2 bg-[#C9A227] transition-[width] duration-300 group-hover:w-1/2" />
          </Link>
          {menuVisible && (
            <Link
              to="/menu"
              data-testid="hero-view-menu-button"
              className="inline-flex items-center gap-2 rounded-full border-2 border-[#C9A227] px-9 py-3 font-medium text-[#9B2C2C] transition-colors hover:bg-[#C9A227] hover:text-white"
            >
              View Full Menu
            </Link>
          )}
          <a
            href={SITE.mapsLink}
            target="_blank"
            rel="noreferrer"
            data-testid="hero-directions-button"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-[#9B2C2C] underline-offset-4 transition-colors hover:text-[#7A1D28] hover:underline"
          >
            <MapPin size={15} /> Get Directions
          </a>
        </motion.div>
      </motion.div>

      <div className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2">
        <motion.div
          animate={reduce ? {} : { y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
          className="h-10 w-[2px] bg-gradient-to-b from-[#9B2C2C]/60 to-transparent"
        />
      </div>
    </section>
  );
};

const Chapter = ({ ch, flip }) => (
  <div className={`grid items-center gap-10 lg:grid-cols-2 lg:gap-16 ${flip ? "" : ""}`}>
    <Reveal className={flip ? "lg:order-2" : ""}>
      <div className="relative">
        <div className="absolute -inset-3 rounded-[18px] border border-[#C9A227]/40" aria-hidden="true" />
        <div className="overflow-hidden rounded-[12px] shadow-[0_24px_60px_rgba(155,44,44,0.16)]">
          <motion.img
            src={ch.img}
            alt={ch.alt}
            data-testid={`chapter-${ch.no}-image`}
            className="aspect-[4/3] w-full object-cover"
            loading="lazy"
            initial={{ scale: 1.12 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
          />
        </div>
        <Blossom className="absolute -left-5 -top-5 h-10 w-10 text-[#E8B4B8]" />
      </div>
    </Reveal>
    <div className={flip ? "lg:order-1" : ""}>
      <Reveal>
        <span data-testid={`chapter-${ch.no}-number`} className="font-serif text-7xl font-black leading-none text-[#E8B4B8] md:text-8xl">
          {ch.no}
        </span>
      </Reveal>
      <Reveal delay={0.1}>
        <h2 className="mt-3 font-serif text-3xl font-bold text-[#9B2C2C] md:text-4xl">{ch.title}</h2>
        <Flourish className="mt-4 h-4 w-40" />
      </Reveal>
      <Reveal delay={0.18}>
        <p className="mt-5 max-w-md text-base leading-relaxed text-[#2C2C2C] md:text-lg">{ch.text}</p>
      </Reveal>
    </div>
  </div>
);

const Signatures = () => {
  const { menuVisible } = useSettings();
  const picks = [
    { ...MENU[4].items[4], cat: "Biryani" },
    { ...MENU[1].items[4], cat: "Initiator" },
    { ...MENU[8].items[6], cat: "Pizza" },
    { ...MENU[11].items[6], cat: "Dessert" },
  ];
  return (
    <section data-testid="signatures-section" className="mx-auto max-w-7xl px-5 py-24 md:px-10 md:py-32">
      <Reveal className="text-center">
        <p className="text-xs font-medium uppercase tracking-[0.3em] text-[#C9A227]">From the Kitchen</p>
        <h2 className="mt-3 font-serif text-4xl font-bold text-[#9B2C2C] md:text-5xl">Signature Blooms</h2>
        <div className="mt-5 flex justify-center"><Flourish className="h-4 w-52" /></div>
      </Reveal>
      <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 md:gap-8">
        {picks.map((item, i) => (
          <DishCard key={item.name} item={item} index={i} idPrefix={`signature-${i}`} />
        ))}
      </div>
      <Reveal className="mt-14 text-center">
        {menuVisible ? (
          <Link
            to="/menu"
            data-testid="signatures-full-menu-link"
            className="group inline-flex items-center gap-2 font-serif text-xl italic text-[#9B2C2C] transition-colors hover:text-[#7E2222]"
          >
            Wander through the full menu
            <ArrowRight size={20} className="transition-transform duration-300 group-hover:translate-x-1.5" />
          </Link>
        ) : (
          <Link
            to="/order"
            data-testid="signatures-order-link"
            className="group inline-flex items-center gap-2 font-serif text-xl italic text-[#9B2C2C] transition-colors hover:text-[#7E2222]"
          >
            Order these signatures online
            <ArrowRight size={20} className="transition-transform duration-300 group-hover:translate-x-1.5" />
          </Link>
        )}
      </Reveal>
    </section>
  );
};

export default function Home() {
  return (
    <main>
      <Hero />
      <section data-testid="home-menu-section" id="home-menu" className="border-t border-[#E8B4B8]/40 bg-[#F3D9D9]/50 pt-16">
        <Reveal className="px-5 text-center">
          <p className="text-xs font-medium uppercase tracking-[0.3em] text-[#C9A227]">Digital Menu</p>
          <h2 className="mt-3 font-serif text-4xl font-bold text-[#9B2C2C] md:text-5xl">Explore Our Menu</h2>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-[#B86B77] md:text-base">
            Our full digital menu — live prices, straight from the kitchen. Seated with us? Just browse. Craving at home? Switch to delivery.
          </p>
          <div className="mt-5 flex justify-center"><Flourish className="h-4 w-52" /></div>
        </Reveal>
        <div className="mt-6">
          <Order embedded />
        </div>
      </section>
      <Marquee />
      <section data-testid="manifesto-section" className="mx-auto flex max-w-7xl flex-col gap-28 px-5 py-24 md:px-10 md:py-36">
        {CHAPTERS.map((ch, i) => (
          <Chapter key={ch.no} ch={ch} flip={i % 2 === 1} />
        ))}
      </section>
      <Signatures />
    </main>
  );
}
